import { Guid } from "guid-typescript";
import { CrmLostRiskLine } from "../Components/Common/LeadDashboard/LeadDashboard";
import { ApplicationForUserType, CountryRegion, Customer, CustomerDomain, CustomerSetupConfig } from "./CustomerModels";
import { LeadIndustry } from "./DemoModels";
import { Lead } from "./PartnerModels";
import { AzureGDAPRelationship, PostalCode, SystemUser, User } from "./SystemModels";

export class LeadToLive {
    public currentStep: LeadToLiveStep = LeadToLiveStep.None;
    public legalEntityVatManagement: boolean = false;
    public lead: Lead = new Lead();
    public leadToLiveQualificationDetails: LeadToLiveQualificationDetails = new LeadToLiveQualificationDetails();
    
    public customer: Customer = new Customer();
    public customerSetupConfig: CustomerSetupConfig = new CustomerSetupConfig();
    public customerDomains: CustomerDomain[] = [];
    public adminUser: SystemUser = new SystemUser();
    public partnerResellerLink: string = "";
    public gdapAdminRelationship: AzureGDAPRelationship = new AzureGDAPRelationship();
    public gdapDynamics365Relationship: AzureGDAPRelationship = new AzureGDAPRelationship();
}

export class LeadToLiveQualificationDetails {
    public businessNeedsRiskLines: CrmLostRiskLine[] = [];
    public businessNeedsNotesRiskLines: CrmLostRiskLine[] = [];
    public gapRiskLines: CrmLostRiskLine[] = [];
    public migrationRiskLines: MigrationLostRiskLineDetail[] = [];
    public leadIndustries: LeadIndustry[] = [];
    public leadQuoteDetails: LeadQuoteDetails[] = [];
}

export class MigrationLostRiskLineDetail {
    public id: number = 0;
    public description: string = '';
    public selected: boolean = false;
}

export class LeadQuoteDetails {
    public itemId: string = "";
    public description: string = '';
    public extendedDescription: string = '';
    public notes: string = '';
    public selected: boolean = false;
    public mandatory: boolean = false;
    public group: string = '';
    public base: boolean = false;
    public quantity: number = 0;
    public recurringInterval: string = '';
    public unitPrice: number = 0.0;
    public prices: ItemPrice[] = [];
}

export class ItemPrice {
    public itemId: number = 0;
    public shopSource: ShopSource = ShopSource.Stripe;
    public shopId: string = '';
    public active: boolean = false;
    public currency: string = '';
    public unitAmount: number = 0.0;
    public type: string = '';
    public interval: string = '';
}

export enum DashboardLinkType {
    ExternalLink,
    InternalLink,
    OpenPage,
    MailTo
}

export class DashboardLink {
    public code: string = "";
    public description: string = "";
    public translatedDescription: string = "";
    public toolTipText: string = "";
    public translatedToolTipText: string = "";
    public order: number = 0;
    public active: boolean = false;
    public enabled: boolean = true;
    public forUserType: ApplicationForUserType = ApplicationForUserType.AdminUser;
    public groupCode: string = "";
    public groupDescription: string = "";
    public translatedGroupDescription: string = "";
    public iconName: string = "";
    public linkType: DashboardLinkType = DashboardLinkType.ExternalLink;
    public linkUrl: string = "";
    public hightlight: boolean = false; //Only frontend
}

export enum DashboardLinkImportance {
    Normal,
    Additional
}

export enum PriceListType {
    Monthly = "month",
    Yearly = "year",
    OneTime = "one_time",
}

export class ErrorResponse {
    constructor(code: number, message: string) {
        this.code = code;
        this.message = message;
    }

    public code: number = 0;
    public message: string = "";
}

export class RegisterError {
    public hasError: boolean = false;
    public step: LeadToLiveStep = LeadToLiveStep.None;
    public fieldErrors: RegisterFieldErrors[] = [];
    public errorMessage: string = "";
}

export class RegisterFieldErrors {
    public key: string = "";
    public error: string = "";
}

export class AuthResponse {
    public success: boolean = false;
    public value: string = "";
}

export class RegisterGDAPMailMessage {
    mailAddress: string = "";
    url: string = "";
}

export enum ShopSource {
    Stripe
}

export enum LeadToLiveStep {
    None,
    VAT,
    CompanyData,
    Welcome,
    Goals,
    Configuration,
    Migrations,
    Scenario,
    TrialEvaluation,
    TenantMode,
    CspCustomerActive,
    CSPReseller,
    GDAPAdmin,
    GDAPDyn365,
    Complete
}

export enum ContractSemaphore
{
    Red,
    Yellow,
    Green,
    White
}

export class DigitalContractSignLink
{
    public entityRelation: number = 0;
    public digitalContactId: number = 0;
    public rowNumber: number = 0;
    public signLink: string = "";
    public signed: boolean = false;
    public signedBy: string = "";
    public signedAt: Date = new Date();
    public stepId: string = "";
    public stepOrderIndex: number = 0;
    public stepFirstName: string = "";
    public stepLastName: string = "";
    public stepEmail: string = "";
}

export class ApplicationLogEntry
{
    public id: string = Guid.EMPTY;
    public type: ApplicationLogEntryType = ApplicationLogEntryType.Information;
    public source: string = "";
    public message: string = "";
    public stackTrace: string = "";
    public userId: number = 0;
}

export enum ApplicationLogEntryType
{
    Information,
    Error,
    Warning
}

export class CommonSubscriptionDetails {
    public subscriptionId: number = 0;
    public itemId: number = 0;
    public description: string = '';
    public extendedDescription: string = '';
    public notes: string = '';
    public selected: boolean = false;
    public mandatory: boolean = false;
    public group: string = '';
    public base: boolean = false;
    public hidden: boolean = false;
    public quantity: number = 0;
    public recurringInterval: string = '';
    public unitPrice: number = 0.0;
    public prices: ItemPrice[] = [];
}